import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const UNITS = ['Adet', 'Metre', 'Saat', 'Gün', 'Adam/Gün', 'Paket', 'Set', 'Kutu'];
const emptyForm = { name: '', description: '', items: [] };
const MAX_RESULTS = 30;

const norm = (s) => (s || '').toLocaleLowerCase('tr');
const productLabel = (p) => (p.code ? `${p.code} — ${p.name}` : p.name);

// Kod, ad, marka veya kategoriye göre aranan ürün kutusu; seçilen ürün onSelect ile döner
function ProductSearch({ products, onSelect }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const words = norm(query).split(/\s+/).filter(Boolean);
  const results = words.length === 0 ? [] : products
    .filter(p => {
      const hay = norm(`${p.code} ${p.name} ${p.brand || ''} ${p.category || ''}`);
      return words.every(w => hay.includes(w));
    })
    .slice(0, MAX_RESULTS);

  const choose = (p) => {
    onSelect(p);
    setQuery('');
    setActive(0);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive(a => Math.min(a + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      // Formun gönderilmesini engelle; vurgulu ürünü ekle
      e.preventDefault();
      if (results[active]) choose(results[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} className="relative w-96">
      <input value={query} placeholder="🔍 Ürün ara (kod, ad, marka) ve ekle..."
        onChange={e => { setQuery(e.target.value); setActive(0); setOpen(true); }}
        onFocus={() => setOpen(true)} onKeyDown={onKeyDown}
        className="w-full border rounded px-3 py-1.5 text-sm" />
      {open && words.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto bg-white border rounded shadow-lg text-sm">
          {results.length === 0 ? (
            <li className="px-3 py-2 text-gray-400">Eşleşen ürün yok</li>
          ) : results.map((p, i) => (
            <li key={p.id} onMouseDown={e => { e.preventDefault(); choose(p); }} onMouseEnter={() => setActive(i)}
              className={`px-3 py-2 cursor-pointer ${i === active ? 'bg-blue-50' : ''}`}>
              <div className="text-gray-800">{productLabel(p)}</div>
              <div className="text-xs text-gray-400">{[p.brand, p.category, p.unit].filter(Boolean).join(' · ')}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function HakedisTemplates() {
  const navigate = useNavigate();
  const isAdmin = !!JSON.parse(localStorage.getItem('user') || '{}').is_admin;

  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // null → editör kapalı, 'new' → yeni şablon, sayı → düzenlenen şablon id
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [expandedId, setExpandedId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/hakedis-templates/');
      setTemplates(res.data);
    } catch (err) {
      console.error('Yükleme hatası:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/api/products/').then(res => setProducts(res.data)).catch(() => setProducts([]));
  }, [isAdmin]);

  const productMap = new Map(products.map(p => [p.id, p]));

  const openNew = () => {
    setForm(emptyForm);
    setEditingId('new');
  };

  const openEdit = (t) => {
    setForm({
      name: t.name,
      description: t.description || '',
      items: t.items.map(i => ({
        product_id: i.product_id, product_name: i.product_name, description: i.description || '',
        quantity: i.quantity, unit: i.unit || 'Adet', section_title: i.section_title,
      })),
    });
    setEditingId(t.id);
  };

  const closeEditor = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const addProduct = (product) => setForm(f => ({
    ...f,
    items: [...f.items, {
      product_id: product.id, product_name: product.name, description: '',
      quantity: 1, unit: product.unit || 'Adet', section_title: false,
    }]
  }));

  const addSection = () => setForm({
    ...form,
    items: [...form.items, { product_id: null, description: 'YENİ BÖLÜM BAŞLIĞI', quantity: 0, unit: '-', section_title: true }]
  });

  const updateItem = (index, field, value) => {
    const items = [...form.items];
    items[index] = { ...items[index], [field]: value };
    setForm({ ...form, items });
  };

  const removeItem = (index) => setForm({ ...form, items: form.items.filter((_, i) => i !== index) });

  const moveItem = (index, direction) => {
    const items = [...form.items];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setForm({ ...form, items });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description || null,
        items: form.items.map(i => ({
          product_id: i.section_title ? null : i.product_id,
          description: i.section_title ? i.description : null,
          quantity: parseFloat(i.quantity) || 0,
          unit: i.unit,
          section_title: i.section_title,
        })),
      };
      if (editingId === 'new') {
        await api.post('/api/hakedis-templates/', payload);
      } else {
        await api.put(`/api/hakedis-templates/${editingId}`, payload);
      }
      closeEditor();
      reload();
    } catch (err) {
      alert('Hata: ' + JSON.stringify(err.response?.data?.detail || 'Bilinmeyen hata'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`"${t.name}" şablonunu silmek istediğinize emin misiniz?`)) return;
    await api.delete(`/api/hakedis-templates/${t.id}`);
    if (editingId === t.id) closeEditor();
    reload();
  };

  if (loading) return <div className="text-center py-20 text-gray-400">Yükleniyor...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Hakediş Şablonları</h2>
          <p className="text-sm text-gray-500 mt-1">
            Teklifsiz hakedişi hazır ürün listesiyle doldurmak için kullanılır. Fiyatlar uygulama anında müşteriye özel / varsayılan fiyattan gelir.
          </p>
        </div>
        <div className="flex items-center gap-4">
          {isAdmin && editingId === null && (
            <button onClick={openNew} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium text-sm">
              + Yeni Şablon
            </button>
          )}
          <button onClick={() => navigate('/hakedisler')} className="text-gray-500 hover:text-gray-700">← Geri</button>
        </div>
      </div>

      {editingId !== null && (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="font-semibold text-gray-700 mb-4">{editingId === 'new' ? 'Yeni Şablon' : 'Şablon Düzenle'}</h3>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Şablon Adı *</label>
              <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full border rounded px-3 py-2" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
              <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full border rounded px-3 py-2" />
            </div>
          </div>

          <div className="flex justify-between items-center mb-3 gap-4">
            <h4 className="font-semibold text-gray-700 text-sm">Kalemler</h4>
            <div className="flex gap-2 items-center">
              <ProductSearch products={products} onSelect={addProduct} />
              <button type="button" onClick={addSection} className="bg-gray-600 text-white px-3 py-1.5 rounded hover:bg-gray-700 text-sm">+ Bölüm Başlığı</button>
            </div>
          </div>
          <table className="w-full text-sm mb-4">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2 w-10"></th>
                <th className="text-left px-2 py-2 text-gray-500">Ürün</th>
                <th className="text-left px-2 py-2 text-gray-500">Miktar</th>
                <th className="text-left px-2 py-2 text-gray-500">Birim</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {form.items.map((item, i) => (
                item.section_title ? (
                  <tr key={i} className="bg-gray-700">
                    <td className="px-2 py-2">
                      <div className="flex flex-col gap-0.5">
                        <button type="button" onClick={() => moveItem(i, -1)} className="text-gray-300 hover:text-white text-xs">▲</button>
                        <button type="button" onClick={() => moveItem(i, 1)} className="text-gray-300 hover:text-white text-xs">▼</button>
                      </div>
                    </td>
                    <td colSpan={3} className="px-3 py-2">
                      <input value={item.description} onChange={e => updateItem(i, 'description', e.target.value)}
                        className="w-full bg-transparent text-white font-bold uppercase tracking-wider border-b border-gray-500 focus:outline-none" />
                    </td>
                    <td className="px-2 py-2">
                      <button type="button" onClick={() => removeItem(i)} className="text-red-300 hover:text-red-100 font-bold text-lg">×</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-2 py-2">
                      <div className="flex flex-col gap-0.5">
                        <button type="button" onClick={() => moveItem(i, -1)} className="text-gray-400 hover:text-gray-600 text-xs">▲</button>
                        <button type="button" onClick={() => moveItem(i, 1)} className="text-gray-400 hover:text-gray-600 text-xs">▼</button>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-gray-800">
                      {productMap.has(item.product_id)
                        ? productLabel(productMap.get(item.product_id))
                        : <span className="text-red-500">{item.product_name || `#${item.product_id}`} (ürün bulunamadı)</span>}
                    </td>
                    <td className="px-2 py-2">
                      <input type="number" step="0.5" value={item.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} className="border rounded px-2 py-1 w-20" />
                    </td>
                    <td className="px-2 py-2">
                      <select value={item.unit} onChange={e => updateItem(i, 'unit', e.target.value)} className="border rounded px-1 py-1 text-xs">
                        {[...new Set([...UNITS, item.unit])].map(u => <option key={u}>{u}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-2">
                      <button type="button" onClick={() => removeItem(i)} className="text-red-500 hover:text-red-700 font-bold text-lg">×</button>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
          {form.items.length === 0 && (
            <div className="text-center py-6 text-gray-400">Ürün eklemek için yukarıdaki arama kutusunu kullanın</div>
          )}
          <div className="flex gap-3">
            <button type="submit" disabled={saving} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
            <button type="button" onClick={closeEditor} className="bg-gray-100 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-200 font-medium">İptal</button>
          </div>
        </form>
      )}

      {templates.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-400">
          <div className="text-4xl mb-3">📑</div>
          <div>Henüz şablon oluşturulmamış</div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Şablon</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Açıklama</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Kalem</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-gray-500">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {templates.map(t => (
                <Fragment key={t.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">{t.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{t.description || '-'}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-600">{t.items.filter(i => !i.section_title).length}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 justify-center">
                        <button onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}
                          className="text-gray-600 hover:text-gray-800 font-medium text-xs px-2 py-1 rounded hover:bg-gray-100">
                          {expandedId === t.id ? '▲ Gizle' : '▼ Kalemler'}
                        </button>
                        {isAdmin && (
                          <>
                            <button onClick={() => openEdit(t)}
                              className="text-blue-600 hover:text-blue-800 font-medium text-xs px-2 py-1 rounded hover:bg-blue-50">
                              ✏️ Düzenle
                            </button>
                            <button onClick={() => handleDelete(t)}
                              className="text-red-500 hover:text-red-700 font-medium text-xs px-2 py-1 rounded hover:bg-red-50">
                              🗑 Sil
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedId === t.id && (
                    <tr>
                      <td colSpan={4} className="px-8 py-3 bg-gray-50">
                        <ul className="text-sm space-y-1">
                          {t.items.map(i => i.section_title ? (
                            <li key={i.id} className="font-bold text-gray-700 uppercase tracking-wider pt-1">{i.description}</li>
                          ) : (
                            <li key={i.id} className="text-gray-600">{i.product_name} — {i.quantity} {i.unit}</li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
